import { Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerForm from './pages/CustomerForm';
import Employees from './pages/Employees';
import EmployeeForm from './pages/EmployeeForm';
import ClauseLibrary from './pages/ClauseLibrary';
import KeyFields from './pages/KeyFields';
import MsaList from './pages/MsaList';
import MsaForm from './pages/MsaForm';
import SowList from './pages/SowList';
import SowForm from './pages/SowForm';
import Engagements from './pages/Engagements';
import EngagementForm from './pages/EngagementForm';
import BillingHome from './pages/BillingHome';
import BillingListing from './pages/BillingListing';
import InvoiceForm from './pages/InvoiceForm';
import InvoicePrint from './pages/InvoicePrint';
import BilledMatrix from './pages/BilledMatrix';
import EmployeePerformance from './pages/EmployeePerformance';
import BillingDashboard from './pages/BillingDashboard';
import TimesheetList from './pages/TimesheetList';
import TimesheetForm from './pages/TimesheetForm';
import Approvals from './pages/Approvals';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import './App.css';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('ErrorBoundary caught:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="page-content" style={{ padding: 24 }}>
          <h2 style={{ color: '#e53e3e' }}>Something went wrong on this screen.</h2>
          <p style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', background: '#fff5f5', padding: 12, borderRadius: 6, fontSize: 12 }}>
            {String(this.state.error?.message || this.state.error)}
            {'\n\n'}
            {this.state.error?.stack || ''}
          </p>
          <button className="btn btn-primary" onClick={() => this.setState({ error: null })}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <div className="page-content"><h2>Access Denied</h2><p>You do not have permission to view this page.</p></div>;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<PrivateRoute><ErrorBoundary><Layout /></ErrorBoundary></PrivateRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/new" element={<CustomerForm />} />
            <Route path="customers/:id" element={<CustomerForm />} />
            <Route path="employees" element={<Employees />} />
            <Route path="employees/new" element={<EmployeeForm />} />
            <Route path="employees/:id" element={<EmployeeForm />} />
            <Route path="clauses" element={<ClauseLibrary />} />
            <Route path="key-fields" element={<KeyFields />} />
            <Route path="msa" element={<MsaList />} />
            <Route path="msa/new" element={<MsaForm />} />
            <Route path="msa/:id" element={<MsaForm />} />
            <Route path="sow" element={<SowList />} />
            <Route path="sow/new" element={<SowForm />} />
            <Route path="sow/:id" element={<SowForm />} />
            <Route path="engagements" element={<Engagements />} />
            <Route path="engagements/new" element={<EngagementForm />} />
            <Route path="engagements/:rfp" element={<EngagementForm />} />
            <Route path="invoices" element={<BillingHome />} />
            <Route path="invoices/customer/:customerId" element={<BillingListing />} />
            <Route path="invoices/view/:id" element={<InvoicePrint />} />
            <Route path="invoices/:id" element={<InvoiceForm />} />
            <Route path="timesheets" element={<TimesheetList />} />
            <Route path="timesheets/new" element={<TimesheetForm />} />
            <Route path="timesheets/:id" element={<TimesheetForm />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="reports/billed-matrix" element={<BilledMatrix />} />
            <Route path="reports/employee-performance" element={<EmployeePerformance />} />
            <Route path="dashboards/billing" element={<BillingDashboard />} />
            <Route path="users" element={<PrivateRoute roles={['Admin']}><Users /></PrivateRoute>} />
            <Route path="settings" element={<PrivateRoute roles={['Admin','HR Manager']}><Settings /></PrivateRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
